import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-expenses-wave2';

export type ReservedPurpose = components['schemas']['ReservedPurpose'];

/** The problem codes the section names in words; anything else reads as «non salvata». */
const KNOWN = [
  'PERSON_NOT_IN_ORGANIZATION',
  'PURPOSE_NOT_RESTRICTED',
  'PEOPLE_UNAVAILABLE',
] as const;
export type ReservedProblem = (typeof KNOWN)[number] | 'OTHER';

export type Outcome<T> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly code: ReservedProblem };

type Run<T> = () => Promise<{ data?: T; error?: unknown; response: Response }>;

async function call<T>(run: Run<T>): Promise<Outcome<T>> {
  try {
    const { data, error, response } = await run();
    if (response.ok && data !== undefined) return { ok: true, value: data };
    const code =
      typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';
    return {
      ok: false,
      code: (KNOWN as readonly string[]).includes(code) ? (code as ReservedProblem) : 'OTHER',
    };
  } catch {
    return { ok: false, code: 'OTHER' };
  }
}

export async function listReservedPurposes(): Promise<Outcome<readonly ReservedPurpose[]>> {
  const outcome: Outcome<{ items: ReservedPurpose[] }> = await call(() =>
    apiClient.GET('/v1/expenses/purposes/reserved'),
  );
  return outcome.ok ? { ok: true, value: outcome.value.items } : outcome;
}

export function setReservedPeople(
  code: string,
  userIds: readonly string[],
): Promise<Outcome<ReservedPurpose>> {
  return call(() =>
    apiClient.PUT('/v1/expenses/purposes/{code}/reserved', {
      params: { path: { code } },
      body: { userIds: [...userIds] },
    }),
  );
}
